import {
  ComponentDefinitionSchema,
  StandardComponentDefinitionSchema
} from "./chunk-WJYTQIJN.js";
import {
  ReportPropsSchema,
  STANDARD_COMPONENTS_REGISTRY,
  createComponentSchemaObject
} from "./chunk-JETULU6Z.js";
import {
  collectDocxRendererErrors
} from "./chunk-HDFGXHUB.js";
import {
  CustomComponentDefinitionSchema
} from "./chunk-7GGTZ4TA.js";

// src/validation/unified/error-transformer.ts
import {
  isObjectSchema,
  getObjectSchemaPropertyNames,
  getLiteralValue,
  createErrorConfig,
  formatErrorMessage,
  ERROR_EMOJIS
} from "@json-to-office/shared";
function generateEnhancedMessage(error, _config) {
  const typeStr = String(error.type || "");
  const path = error.path || "root";
  if (typeStr === "62" || typeStr === "union") {
    return generateUnionErrorMessage(error);
  }
  if (error.message?.includes("additionalProperties")) {
    return generateAdditionalPropertiesMessage(error);
  }
  if (error.message?.includes("Required property")) {
    return generateRequiredPropertyMessage(error);
  }
  if (typeStr === "string" || typeStr === "number" || typeStr === "boolean" || typeStr === "array" || typeStr === "object") {
    return generateTypeMismatchMessage(error);
  }
  if (typeStr === "literal") {
    return generateLiteralErrorMessage(error);
  }
  if (typeStr === "pattern" || typeStr === "RegExp") {
    return generatePatternErrorMessage(error);
  }
  return `At ${path}: ${error.message}`;
}
function generateUnionErrorMessage(error) {
  const path = error.path || "root";
  const value = error.value;
  if (path === "root" || path === "/" || path === "/jsonDefinition") {
    if (value && typeof value === "object") {
      if ("name" in value) {
        const name = value.name;
        if (name === "docx") {
          return "Document structure appears valid but contains invalid component configurations. Check each component for errors.";
        }
        return `Unknown document name '${name}'. Expected 'docx'.`;
      }
      const valueAny = value;
      if ("children" in value && Array.isArray(valueAny.children)) {
        return `Document is missing required 'name' field. Add "name": "docx" at the root level.`;
      }
      if ("name" in value || "styles" in value) {
        return "This appears to be a theme configuration. Use --type theme or ensure proper document structure.";
      }
    }
    return 'Invalid document structure. Expected a document with name="docx" or a theme configuration.';
  }
  if (path.includes("/children/")) {
    if (value && typeof value === "object" && "name" in value) {
      const componentType = value.name;
      return `Invalid component configuration for type '${componentType}'. Check that all required fields are present and correctly formatted.`;
    }
    return 'Invalid component structure. Each component must have a "name" field and valid configuration.';
  }
  return `Value at ${path} doesn't match any of the expected formats. Check the structure and required fields.`;
}
function generateAdditionalPropertiesMessage(error) {
  const path = error.path || "root";
  const value = error.value;
  if (typeof value === "object" && value !== null) {
    const schema = error.schema;
    if (schema && isObjectSchema(schema)) {
      const knownProps = getObjectSchemaPropertyNames(schema);
      const actualProps = Object.keys(value);
      const unknownProps = actualProps.filter((p) => !knownProps.includes(p));
      if (unknownProps.length > 0) {
        return `Unknown properties at ${path}: ${unknownProps.join(", ")}. Allowed properties are: ${knownProps.join(", ")}`;
      }
    }
  }
  return `Additional properties not allowed at ${path}. Check for typos or unsupported fields.`;
}
function generateRequiredPropertyMessage(error) {
  const path = error.path || "root";
  const match = error.message?.match(/Required property '([^']+)'/);
  if (match) {
    const propName = match[1];
    return `Missing required field '${propName}' at ${path}. This field is mandatory for this configuration.`;
  }
  return `Missing required property at ${path}. Check that all mandatory fields are present.`;
}
function generateTypeMismatchMessage(error) {
  const path = error.path || "root";
  const expectedType = String(error.type);
  const actualType = Array.isArray(error.value) ? "array" : typeof error.value;
  if (path.includes("alignment")) {
    return `Invalid alignment value at ${path}. Expected one of: left, center, right, justify`;
  }
  if (path.includes("color")) {
    return `Invalid color value at ${path}. Use hex format (#RRGGBB), rgb(r,g,b), or a named color`;
  }
  if (path.includes("fontSize") || path.includes("size")) {
    return `Invalid size value at ${path}. Expected a number (in points)`;
  }
  if (path.includes("margin") || path.includes("padding") || path.includes("spacing")) {
    return `Invalid spacing value at ${path}. Expected a number or spacing object with top/bottom/left/right`;
  }
  return `Type mismatch at ${path}: Expected ${expectedType} but got ${actualType}`;
}
function generateLiteralErrorMessage(error) {
  const path = error.path || "root";
  const expected = error.schema ? JSON.stringify(getLiteralValue(error.schema)) : "specific value";
  const actual = JSON.stringify(error.value);
  return `Invalid value at ${path}: Expected exactly ${expected} but got ${actual}`;
}
function generatePatternErrorMessage(error) {
  const path = error.path || "root";
  if (path.includes("email")) {
    return `Invalid email format at ${path}. Use format: user@example.com`;
  }
  if (path.includes("url") || path.includes("link")) {
    return `Invalid URL format at ${path}. Use format: https://example.com`;
  }
  if (path.includes("date")) {
    return `Invalid date format at ${path}. Use ISO format: YYYY-MM-DD`;
  }
  if (path.includes("phone")) {
    return `Invalid phone number format at ${path}`;
  }
  return `Value at ${path} doesn't match the required pattern`;
}
function transformValueError(error, jsonString, config) {
  const formatterConfig = createErrorConfig(config);
  const enhancedMessage = generateEnhancedMessage(error, formatterConfig);
  const baseError = {
    path: error.path || "root",
    message: formatErrorMessage(
      enhancedMessage || error.message,
      formatterConfig
    ),
    code: String(error.type || "validation_error"),
    value: error.value
  };
  if (formatterConfig.includeSuggestions) {
    const suggestion = getSuggestion(error, formatterConfig);
    if (suggestion) {
      baseError.suggestion = formatErrorMessage(suggestion, formatterConfig);
    }
  }
  if (jsonString && error.path) {
    const position = calculatePosition(jsonString, error.path);
    if (position) {
      baseError.line = position.line;
      baseError.column = position.column;
    }
  }
  return baseError;
}
function transformValueErrors(errors, options) {
  const maxErrors = options?.maxErrors ?? Number.MAX_SAFE_INTEGER;
  const result = [];
  const seenPaths = /* @__PURE__ */ new Set();
  for (const error of errors) {
    if (result.length >= maxErrors) break;
    const errorKey = `${error.path}:${error.type}`;
    if (!seenPaths.has(errorKey)) {
      seenPaths.add(errorKey);
      result.push(transformValueError(error, options?.jsonString, void 0));
    }
  }
  return result;
}
function calculatePosition(jsonString, path) {
  try {
    const pathParts = path.split("/").filter(Boolean);
    if (pathParts.length === 0) {
      return { line: 1, column: 1 };
    }
    const lastPart = pathParts[pathParts.length - 1];
    const searchPattern = `"${lastPart}"`;
    const index = jsonString.indexOf(searchPattern);
    if (index === -1) {
      return { line: 1, column: 1 };
    }
    const beforeError = jsonString.substring(0, index);
    const lines = beforeError.split("\n");
    const line = lines.length;
    const column = lines[lines.length - 1].length + 1;
    return { line, column };
  } catch {
    return null;
  }
}
function getSuggestion(error, config) {
  const { type, path, value } = error;
  const typeStr = String(type);
  if (typeStr === "62" || typeStr === "union") {
    if (path === "root" || path === "/") {
      if (value && typeof value === "object") {
        if (!("name" in value)) {
          const msg = 'Add a "name" field with value "docx" for documents';
          return config.includeEmojis ? `${ERROR_EMOJIS.FIX} ${msg}` : msg;
        }
        if ("props" in value && typeof value.props === "object") {
          const knownFields = getObjectSchemaPropertyNames(ReportPropsSchema);
          return `Review the props section for unsupported fields. Allowed fields: ${knownFields.join(", ")}`;
        }
      }
      return 'Ensure the document has proper structure: { "name": "docx", "props": {...}, "children": [...] }';
    }
    if (path?.includes("/children/")) {
      return 'Check that the component has a valid "name" and all required fields for that component type';
    }
    return "Review the structure and ensure all required fields are present with correct types";
  }
  if (error.message?.includes("additionalProperties")) {
    return "Remove any unknown or unsupported fields. Check documentation for allowed properties.";
  }
  if (error.message?.includes("Required property")) {
    return "Add the missing required field to fix this error";
  }
  if (typeStr === "string") {
    if (path?.includes("alignment")) {
      return "Use one of: left, center, right, justify";
    }
    if (path?.includes("color")) {
      return "Use a valid color format (hex: #RRGGBB, rgb: rgb(r,g,b), or named color)";
    }
    return "Provide a text string value";
  }
  if (typeStr === "number") {
    if (path?.includes("fontSize") || path?.includes("size")) {
      return "Use a number in points (e.g., 12, 14, 16)";
    }
    if (path?.includes("margin") || path?.includes("padding")) {
      return "Use a number for spacing in points";
    }
    return "Provide a numeric value";
  }
  if (typeStr === "boolean") {
    return "Use true or false (without quotes)";
  }
  if (typeStr === "array") {
    if (path?.includes("children")) {
      return 'Provide an array of component objects, each with a "name" field';
    }
    return "Provide an array/list of values using square brackets []";
  }
  if (typeStr === "object") {
    return "Provide an object with key-value pairs using curly braces {}";
  }
  if (typeStr === "literal") {
    const expected = error.schema ? JSON.stringify(getLiteralValue(error.schema)) : "specific value";
    return `Use exactly this value: ${expected}`;
  }
  if (typeStr === "pattern" || typeStr === "RegExp") {
    if (path?.includes("email")) {
      return "Use valid email format: user@example.com";
    }
    if (path?.includes("url")) {
      return "Use valid URL format: https://example.com";
    }
    if (path?.includes("date")) {
      return "Use ISO date format: YYYY-MM-DD";
    }
    return "Ensure the value matches the required format";
  }
  if (path?.includes("name") || path?.includes("type")) {
    return "Use a valid component name: docx, section, columns, heading, paragraph, image, statistic, table, list, toc, text-box, highcharts, or visual";
  }
  return void 0;
}

// src/validation/unified/deep-validator.ts
import { Value } from "@sinclair/typebox/value";
var COMPONENT_SCHEMAS = Object.fromEntries([
  ...STANDARD_COMPONENTS_REGISTRY.map((c) => [c.name, c.propsSchema]),
  ["custom", CustomComponentDefinitionSchema]
]);
var EMBEDDED_REGIONS = new Map(
  STANDARD_COMPONENTS_REGISTRY.filter(
    (c) => (c.embeddedComponents?.length ?? 0) > 0
  ).map((c) => [c.name, c.embeddedComponents])
);
var ROOT_COMPONENT_NAMES = new Set(
  STANDARD_COMPONENTS_REGISTRY.filter(
    (c) => Boolean(c.special?.hasSchemaField)
  ).map((c) => c.name)
);
var ALLOWED_SIBLING_KEYS = new Map(
  STANDARD_COMPONENTS_REGISTRY.map((c) => [
    c.name,
    /* @__PURE__ */ new Set([
      "name",
      "id",
      "enabled",
      "props",
      ...c.hasChildren ? ["children"] : [],
      ...c.special?.hasSchemaField ? ["$schema", "renderer"] : []
    ])
  ])
);
var SIBLING_KEY_TYPES = {
  id: "string",
  enabled: "boolean",
  $schema: "string",
  renderer: "string"
};
function collectSiblingKeyErrors(node, path, opts, errors) {
  const allowed = typeof node.name === "string" ? ALLOWED_SIBLING_KEYS.get(node.name) : void 0;
  if (!allowed) return;
  for (const key of Object.keys(node)) {
    if (!allowed.has(key)) {
      if (opts.allowUnknownFields) continue;
      errors.push({
        path: `${path}/${key}`,
        message: `Unknown key "${key}" on component "${node.name}". Allowed keys: ${[
          ...allowed
        ].join(", ")}.`,
        code: "unknown_key"
      });
      continue;
    }
    const expected = SIBLING_KEY_TYPES[key];
    if (expected && typeof node[key] !== expected) {
      errors.push({
        path: `${path}/${key}`,
        message: `Key "${key}" on component "${node.name}" must be a ${expected}.`,
        code: "invalid_type"
      });
    }
  }
}
var IMAGE_SOURCE_FIELDS = ["path", "base64", "svg"];
function presentImageSources(props) {
  if (!props || typeof props !== "object") return [];
  const p = props;
  return IMAGE_SOURCE_FIELDS.filter((f) => {
    const v = p[f];
    return typeof v === "string" && v.trim().length > 0;
  });
}
function collectImageSourceConflicts(data) {
  const errors = [];
  const visit = (node, path) => {
    if (Array.isArray(node)) {
      node.forEach((item, i) => visit(item, `${path}/${i}`));
      return;
    }
    if (!node || typeof node !== "object") return;
    if (node.name === "image") {
      const present = presentImageSources(node.props);
      if (present.length > 1) {
        errors.push({
          path: `${path}/props`,
          message: `Image component accepts only one source, but found ${present.map((f) => `"${f}"`).join(", ")}. Use exactly one of "path", "base64", or "svg".`,
          code: "mutually_exclusive"
        });
      }
    }
    for (const key of Object.keys(node)) {
      visit(node[key], `${path}/${key}`);
    }
  };
  visit(data, "");
  return errors;
}
var INDENT_COMPONENT_NAMES = /* @__PURE__ */ new Set(["paragraph", "heading"]);
function collectIndentConflicts(data) {
  const errors = [];
  const visit = (node, path) => {
    if (Array.isArray(node)) {
      node.forEach((item, i) => visit(item, `${path}/${i}`));
      return;
    }
    if (!node || typeof node !== "object") return;
    if (INDENT_COMPONENT_NAMES.has(node.name)) {
      const indent = node.props?.indent;
      if (indent && typeof indent === "object" && indent.hanging !== void 0 && indent.firstLine !== void 0) {
        errors.push({
          path: `${path}/props/indent`,
          message: 'Indent accepts either "hanging" or "firstLine", not both. Use exactly one of the two.',
          code: "mutually_exclusive"
        });
      }
    }
    for (const key of Object.keys(node)) {
      visit(node[key], `${path}/${key}`);
    }
  };
  visit(data, "");
  return errors;
}
function collectNoteRevisionConflicts(data) {
  const errors = [];
  const visit = (node, path) => {
    if (Array.isArray(node)) {
      node.forEach((item, i) => visit(item, `${path}/${i}`));
      return;
    }
    if (!node || typeof node !== "object") return;
    const revisedNotes = [];
    if (node.name === "paragraph" && node.props?.revision) {
      for (const kind of ["footnotes", "endnotes"]) {
        revisedNotes.push({
          notes: node.props[kind],
          path: `${path}/props/${kind}`
        });
      }
    }
    if (node.revision && node.content?.name === "paragraph") {
      for (const kind of ["footnotes", "endnotes"]) {
        revisedNotes.push({
          notes: node.content.props?.[kind],
          path: `${path}/content/props/${kind}`
        });
      }
    }
    for (const { notes, path: notesPath } of revisedNotes) {
      if (Array.isArray(notes) && notes.length > 0) {
        const kind = notesPath.endsWith("endnotes") ? "endnotes" : "footnotes";
        errors.push({
          path: notesPath,
          message: `"revision" and "${kind}" cannot apply to the same text. Tracked-change text renders literally, so note markers inside it are not resolved and the note bodies would be dropped. Move the notes to text without a revision.`,
          code: "mutually_exclusive"
        });
      }
    }
    for (const key of Object.keys(node)) {
      visit(node[key], `${path}/${key}`);
    }
  };
  visit(data, "");
  return errors;
}
var UNDASHABLE_BORDER_STYLES = /* @__PURE__ */ new Set(["dashed", "dotted", "double"]);
function collectTextBoxShapeConflicts(data) {
  const errors = [];
  const visit = (node, path) => {
    if (Array.isArray(node)) {
      node.forEach((item, i) => visit(item, `${path}/${i}`));
      return;
    }
    if (!node || typeof node !== "object") return;
    if (node.name === "text-box" && node.props?.renderAs === "shape") {
      const missing = ["width", "height"].filter(
        (axis) => node.props[axis] === void 0
      );
      if (missing.length > 0) {
        errors.push({
          path: `${path}/props/${missing[0]}`,
          message: `A text-box with renderAs "shape" requires ${missing.join(" and ")}: a shape has no autofit, so its size cannot be derived from its content. Give it an explicit size, or use renderAs "table", which grows to fit.`,
          code: "required"
        });
      }
      const border = node.props.style?.border;
      if (border && typeof border === "object") {
        for (const side of ["top", "right", "bottom", "left"]) {
          const style = border[side]?.style;
          if (typeof style === "string" && UNDASHABLE_BORDER_STYLES.has(style)) {
            errors.push({
              path: `${path}/props/style/border/${side}/style`,
              message: `A text-box with renderAs "shape" cannot draw a "${style}" border: a shape outline carries no dash pattern. Use "solid", or use renderAs "table", which draws every border style.`,
              code: "unsupported_value"
            });
          }
        }
      }
    }
    for (const key of Object.keys(node)) {
      visit(node[key], `${path}/${key}`);
    }
  };
  visit(data, "");
  return errors;
}
function deepValidateDocument(data, opts = {}) {
  const allErrors = [];
  if (!data || typeof data !== "object") {
    allErrors.push({
      path: "root",
      message: "Document must be an object",
      code: "invalid_type"
    });
    return allErrors;
  }
  if (!data.name) {
    allErrors.push({
      path: "/name",
      message: 'Missing required field "name"',
      code: "required_property"
    });
  } else if (!ROOT_COMPONENT_NAMES.has(data.name)) {
    const expected = [...ROOT_COMPONENT_NAMES].map((n) => `"${n}"`).join(", ");
    allErrors.push({
      path: "/name",
      message: `Invalid name "${data.name}". Expected ${expected}`,
      code: "invalid_value"
    });
  }
  collectSiblingKeyErrors(data, "", opts, allErrors);
  if (ROOT_COMPONENT_NAMES.has(data.name) && "props" in data) {
    const propsErrors = validateComponentProps(
      data.name,
      data.props,
      "/props",
      opts
    );
    allErrors.push(...propsErrors);
  }
  if (!data.children) {
    allErrors.push({
      path: "/children",
      message: 'Missing required field "children"',
      code: "required_property"
    });
  } else if (!Array.isArray(data.children)) {
    allErrors.push({
      path: "/children",
      message: 'Field "children" must be an array',
      code: "invalid_type"
    });
  }
  walkComponentTree(data, "", opts, allErrors);
  return allErrors;
}
function walkComponentTree(node, path, opts, errors) {
  if (!node || typeof node !== "object") return;
  const validateEntry = (child, childPath, strictStructure) => {
    if (!child || typeof child !== "object") {
      if (strictStructure) {
        errors.push({
          path: childPath,
          message: "Component must be an object",
          code: "invalid_type"
        });
      }
      return;
    }
    if (typeof child.name !== "string" || child.name.length === 0) {
      if (strictStructure) {
        errors.push({
          path: `${childPath}/name`,
          message: 'Component missing required field "name"',
          code: "required_property"
        });
      }
      return;
    }
    if (opts.knownCustomNames?.has(child.name)) return;
    collectSiblingKeyErrors(child, childPath, opts, errors);
    if ("props" in child) {
      errors.push(
        ...validateComponentProps(
          child.name,
          child.props,
          `${childPath}/props`,
          opts
        )
      );
    } else if (child.name !== "custom") {
      errors.push(
        ...validateComponentProps(child.name, {}, `${childPath}/props`, opts)
      );
    }
    walkComponentTree(child, childPath, opts, errors);
  };
  const regions = typeof node.name === "string" ? EMBEDDED_REGIONS.get(node.name) : void 0;
  if (regions && node.props && typeof node.props === "object") {
    for (const region of regions) {
      resolveRegionValues(
        node.props,
        region.path,
        `${path}/props`,
        (value, valuePath) => {
          if (region.arity === "component-array") {
            if (Array.isArray(value)) {
              value.forEach(
                (child, i) => validateEntry(
                  child,
                  `${valuePath}/${i}`,
                  region.reportStructure
                )
              );
            }
          } else if (value && typeof value === "object" && !Array.isArray(value)) {
            validateEntry(value, valuePath, region.reportStructure);
          }
        }
      );
    }
  }
  if (Array.isArray(node.children)) {
    node.children.forEach(
      (child, i) => validateEntry(child, `${path}/children/${i}`, true)
    );
  } else if (node.children != null && path !== "") {
    errors.push({
      path: `${path}/children`,
      message: 'Field "children" must be an array',
      code: "invalid_type"
    });
  }
}
function resolveRegionValues(value, segments, path, visit) {
  if (segments.length === 0) {
    visit(value, path);
    return;
  }
  if (!value || typeof value !== "object") return;
  const [head, ...rest] = segments;
  if (head === "*") {
    if (!Array.isArray(value)) return;
    value.forEach(
      (item, i) => resolveRegionValues(item, rest, `${path}/${i}`, visit)
    );
    return;
  }
  resolveRegionValues(value[head], rest, `${path}/${head}`, visit);
}
function validateComponentProps(componentName, props, basePath, opts = {}) {
  const errors = [];
  const schema = COMPONENT_SCHEMAS[componentName];
  if (!schema) {
    errors.push({
      path: basePath.replace(/\/props$/, "/name"),
      message: `Unknown component "${componentName}"`,
      code: "unknown_component"
    });
    return errors;
  }
  const branch = selectPropsBranch(schema, props);
  const toCheck = opts.allowUnknownFields ? Value.Clean(branch, Value.Clone(props)) : props;
  if (!Value.Check(branch, toCheck)) {
    const valueErrors = [...Value.Errors(branch, toCheck)];
    const transformedErrors = transformValueErrors(valueErrors, {
      maxErrors: 100
    });
    transformedErrors.forEach((error) => {
      const fullPath = error.path === "root" ? basePath : `${basePath}${error.path.startsWith("/") ? error.path : "/" + error.path}`;
      errors.push({
        ...error,
        path: fullPath
      });
    });
  }
  return errors;
}
function selectPropsBranch(schema, props) {
  const branches = schema.anyOf;
  if (!Array.isArray(branches) || branches.length === 0) return schema;
  const claimed = branches.filter((branch) => claimsProps(branch, props));
  if (claimed.length === 1) return claimed[0];
  const candidates = claimed.length > 1 ? claimed : branches;
  let best = candidates[0];
  let fewest = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const count = [...Value.Errors(candidate, props)].length;
    if (count < fewest) {
      fewest = count;
      best = candidate;
    }
  }
  return best;
}
function claimsProps(branch, props) {
  const properties = branch.properties;
  if (!properties) return false;
  const required = new Set(
    branch.required ?? []
  );
  const value = props ?? {};
  for (const [key, propertySchema] of Object.entries(properties)) {
    const literal = propertySchema.const;
    if (literal === void 0) continue;
    const present = value[key] !== void 0;
    if (!present) {
      if (required.has(key)) return false;
      continue;
    }
    if (value[key] !== literal) return false;
  }
  return true;
}
function comprehensiveValidateDocument(data, existingErrors = [], opts = {}) {
  const deepErrors = deepValidateDocument(data, opts);
  const filteredExisting = existingErrors.filter(
    (e) => !isGenericUnionCatchAll(e)
  );
  return deduplicateErrors([...filteredExisting, ...deepErrors]);
}
function isGenericUnionCatchAll(error) {
  const atRoot = !error.path || error.path === "root" || error.path === "/";
  if (!atRoot) return false;
  const msg = error.message || "";
  return /invalid component configurations?/i.test(msg) || /invalid document structure/i.test(msg);
}
function deduplicateErrors(errors) {
  const seen = /* @__PURE__ */ new Set();
  const unique = [];
  for (const error of errors) {
    const key = `${error.path}:${error.message}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(error);
    }
  }
  return unique;
}

// src/validation/unified/base-validator.ts
import { Value as Value2 } from "@sinclair/typebox/value";
import {
  transformValueErrors as transformValueErrors2,
  createJsonParseError,
  formatErrorSummary
} from "@json-to-office/shared";
function validateAgainstSchema(schema, data, options) {
  try {
    if (!Value2.Check(schema, data)) {
      const errors = [...Value2.Errors(schema, data)];
      const transformedErrors = transformValueErrors2(errors, {
        jsonString: options?.jsonString,
        maxErrors: options?.maxErrors || 100
        // Default to 100 if not specified
      });
      return {
        valid: false,
        errors: transformedErrors
      };
    }
    let processedData = data;
    if (options?.clean) {
      processedData = Value2.Clean(schema, Value2.Clone(processedData));
    }
    if (options?.applyDefaults) {
      processedData = Value2.Default(schema, processedData);
    }
    return {
      valid: true,
      data: processedData
    };
  } catch (error) {
    return {
      valid: false,
      errors: [
        {
          path: "root",
          message: error instanceof Error ? error.message : "Unknown validation error",
          code: "validation_exception"
        }
      ]
    };
  }
}
function validateJson(schema, jsonInput, options) {
  if (typeof jsonInput === "string") {
    if (!jsonInput.trim()) {
      return {
        valid: false,
        errors: [
          {
            path: "root",
            message: "Input must be a non-empty string",
            code: "empty_input"
          }
        ],
        isJsonError: true
      };
    }
    let parsed;
    try {
      parsed = JSON.parse(jsonInput);
    } catch (error) {
      if (error instanceof Error) {
        return {
          valid: false,
          errors: [createJsonParseError(error, jsonInput)],
          isJsonError: true
        };
      }
      return {
        valid: false,
        errors: [
          {
            path: "root",
            message: "Failed to parse JSON",
            code: "json_parse_error"
          }
        ],
        isJsonError: true
      };
    }
    const result2 = validateAgainstSchema(schema, parsed, {
      ...options,
      jsonString: jsonInput,
      calculatePosition: true
    });
    return {
      ...result2,
      parsed,
      isJsonError: false
    };
  }
  const result = validateAgainstSchema(schema, jsonInput, options);
  return {
    ...result,
    parsed: jsonInput,
    isJsonError: false
  };
}
function validateBatch(schema, items, options) {
  const results = [];
  const allErrors = [];
  let hasErrors = false;
  for (let i = 0; i < items.length; i++) {
    const result = validateAgainstSchema(schema, items[i], options);
    if (result.valid && result.data) {
      results.push(result.data);
    } else {
      hasErrors = true;
      if (result.errors) {
        const prefixedErrors = result.errors.map((e) => ({
          ...e,
          path: `[${i}]${e.path ? "/" + e.path : ""}`
        }));
        allErrors.push(...prefixedErrors);
      }
    }
    if (options?.maxErrors && allErrors.length >= options.maxErrors) {
      break;
    }
  }
  if (hasErrors) {
    return {
      valid: false,
      errors: allErrors.slice(0, options?.maxErrors)
    };
  }
  return {
    valid: true,
    data: results
  };
}
function validateWithEnhancement(schema, data, enhancer, options) {
  const result = validateAgainstSchema(schema, data, options);
  if (!result.valid && result.errors) {
    result.errors = enhancer(result.errors);
  }
  return result;
}
function createValidator(schema, defaultOptions) {
  return (data, options) => {
    return validateAgainstSchema(schema, data, {
      ...defaultOptions,
      ...options
    });
  };
}
function createJsonValidator(schema, defaultOptions) {
  return (jsonInput, options) => {
    return validateJson(schema, jsonInput, {
      ...defaultOptions,
      ...options
    });
  };
}
function isValidationSuccess(result) {
  return result.valid === true && result.data !== void 0;
}
function getValidationSummary(result) {
  if (result.valid) {
    return "Validation successful";
  }
  if (!result.errors || result.errors.length === 0) {
    return "Validation failed with unknown error";
  }
  return formatErrorSummary(result.errors);
}

// src/validation/unified/document-validator.ts
import { Type } from "@sinclair/typebox";
import { Value as Value3 } from "@sinclair/typebox/value";
import {
  explainRemovedBlockSyntax,
  readBlockDefinitions,
  validateBlockInvocations,
  extractStandardComponentNames
} from "@json-to-office/shared";
var JsonComponentDefinitionSchema = ComponentDefinitionSchema;
var SEMANTIC_COLLECTORS = [
  {
    // A multi-source image passes the structural check: each source is its own
    // optional field.
    why: "image source mutual-exclusivity",
    collect: collectImageSourceConflicts
  },
  {
    why: "indent hanging/firstLine mutual-exclusivity",
    collect: collectIndentConflicts
  },
  {
    // Tracked-change text renders literally, so a note marker inside it never
    // resolves and docx cannot express the pair anyway.
    why: "notes on a revised paragraph",
    collect: collectNoteRevisionConflicts
  },
  {
    // A shape has no autofit and its outline carries no dash pattern, so the
    // request could only ever come back as a table.
    why: "a text box asking for a shape rendering a shape cannot give it",
    collect: collectTextBoxShapeConflicts
  },
  {
    why: "renderer profile compatibility",
    collect: collectDocxRendererErrors
  }
];
function collectSemanticConflicts(document) {
  return SEMANTIC_COLLECTORS.flatMap(({ collect }) => collect(document));
}
function referencesCustomComponent(node, names) {
  if (Array.isArray(node)) {
    return node.some((item) => referencesCustomComponent(item, names));
  }
  if (!node || typeof node !== "object") return false;
  const record = node;
  if (typeof record.name === "string" && names.has(record.name)) return true;
  return Object.values(record).some(
    (value) => referencesCustomComponent(value, names)
  );
}
var containmentRelaxedSchema;
function getContainmentRelaxedSchema() {
  containmentRelaxedSchema ??= Type.Recursive(
    (This) => Type.Union(
      STANDARD_COMPONENTS_REGISTRY.map(
        (component) => createComponentSchemaObject(component, This, This)
      )
    )
  );
  return containmentRelaxedSchema;
}
function resolveEmptyWalk(document, stageOneErrors, options) {
  if (options?.allowUnknownFields === true || options?.knownCustomNames !== void 0 && options.knownCustomNames.size > 0 && referencesCustomComponent(document, options.knownCustomNames) || Value3.Check(getContainmentRelaxedSchema(), document)) {
    return { valid: true, errors: [] };
  }
  const errors = stageOneErrors.length > 0 ? stageOneErrors : [
    {
      path: "root",
      message: "Document does not match the 'docx' document schema.",
      code: "invalid_document"
    }
  ];
  return {
    valid: false,
    errors: [
      ...errors,
      {
        path: "root",
        message: 'The document was rejected by the whole-document schema, but deep validation could not localize the fault. Common causes: an unknown key next to "name", "props" or "children" on some component, or a value at a position the schema does not declare.',
        code: "unlocalized_schema_error"
      }
    ]
  };
}
function deepValidateRejected(document, stageOneErrors, options) {
  const errors = comprehensiveValidateDocument(document, stageOneErrors, {
    allowUnknownFields: options?.allowUnknownFields,
    knownCustomNames: options?.knownCustomNames
  });
  if (errors.length === 0) {
    return resolveEmptyWalk(document, stageOneErrors, options);
  }
  return { valid: false, errors };
}
function validateDocument(data, options) {
  const result = validateAgainstSchema(
    ComponentDefinitionSchema,
    data,
    options
  );
  let finalErrors = result.errors || [];
  let finalValid = result.valid;
  if (!result.valid && data) {
    ({ valid: finalValid, errors: finalErrors } = deepValidateRejected(
      data,
      result.errors || [],
      options
    ));
  }
  const semanticConflicts = [
    ...collectSemanticConflicts(data),
    ...validateBlockInvocations(data, readBlockDefinitions(data), "docx", [
      ...options?.knownCustomNames ?? []
    ])
  ];
  if (semanticConflicts.length > 0) {
    finalErrors = [...finalErrors, ...semanticConflicts];
    finalValid = false;
  }
  finalErrors = explainRemovedBlockSyntax(data, "docx", finalErrors);
  const resolvedData = result.data ?? (finalValid ? data : void 0);
  const documentResult = {
    ...result,
    valid: finalValid,
    documentType: "docx",
    errors: finalErrors,
    data: resolvedData
  };
  if (finalValid) {
    const doc = resolvedData;
    if (doc && doc.children && Array.isArray(doc.children)) {
      const hasCustom = doc.children.some(
        (c) => !isStandardComponentName(c.name)
      );
      documentResult.hasCustomComponents = hasCustom;
    }
  }
  return documentResult;
}
function validateJsonDocument(jsonInput, options) {
  const result = validateJson(
    JsonComponentDefinitionSchema,
    jsonInput,
    options
  );
  let finalErrors = result.errors || [];
  let finalValid = result.valid;
  if (!result.valid && result.parsed) {
    ({ valid: finalValid, errors: finalErrors } = deepValidateRejected(
      result.parsed,
      result.errors || [],
      options
    ));
  }
  const semanticConflicts = [
    ...collectSemanticConflicts(result.parsed),
    ...validateBlockInvocations(
      result.parsed,
      readBlockDefinitions(result.parsed),
      "docx",
      [...options?.knownCustomNames ?? []]
    )
  ];
  if (semanticConflicts.length > 0) {
    finalErrors = [...finalErrors, ...semanticConflicts];
    finalValid = false;
  }
  finalErrors = explainRemovedBlockSyntax(result.parsed, "docx", finalErrors);
  const resolvedData = result.data ?? (finalValid ? result.parsed : void 0);
  const documentResult = {
    ...result,
    valid: finalValid,
    documentType: "docx",
    errors: finalErrors,
    data: resolvedData
  };
  if (finalValid) {
    const doc = resolvedData;
    if (doc && doc.children && Array.isArray(doc.children)) {
      const hasCustom = doc.children.some(
        (c) => !isStandardComponentName(c.name)
      );
      documentResult.hasCustomComponents = hasCustom;
    }
  }
  return documentResult;
}
function isValidDocument(result) {
  return result.valid === true && result.data !== void 0;
}
function getStandardComponentNames() {
  return extractStandardComponentNames(StandardComponentDefinitionSchema);
}
function isStandardComponentName(name) {
  const standardNames = getStandardComponentNames();
  return standardNames.includes(name);
}
function createDocumentValidator(defaultOptions) {
  return {
    validate: (data, options) => validateDocument(data, { ...defaultOptions, ...options }),
    validateJson: (jsonInput, options) => validateJsonDocument(jsonInput, { ...defaultOptions, ...options })
  };
}
var documentValidator = createDocumentValidator({
  clean: true,
  applyDefaults: true,
  maxErrors: 100
  // Collect up to 100 errors to show all validation issues
});
var strictDocumentValidator = createDocumentValidator({
  clean: false,
  applyDefaults: false,
  maxErrors: 100
  // Increased from 10 to show more errors
});
var validateJsonComponent = validateJsonDocument;
var validateDocumentWithSchema = validateDocument;

export {
  transformValueError,
  transformValueErrors,
  collectIndentConflicts,
  collectNoteRevisionConflicts,
  collectTextBoxShapeConflicts,
  deepValidateDocument,
  comprehensiveValidateDocument,
  validateAgainstSchema,
  validateJson,
  validateBatch,
  validateWithEnhancement,
  createValidator,
  createJsonValidator,
  isValidationSuccess,
  getValidationSummary,
  validateDocument,
  validateJsonDocument,
  isValidDocument,
  createDocumentValidator,
  documentValidator,
  strictDocumentValidator,
  validateJsonComponent,
  validateDocumentWithSchema
};
//# sourceMappingURL=chunk-MKBB5XJN.js.map